import {
  affiliateId,
  bookingBase,
  bookingCheckoutPath,
  bookingItineraryPath,
  bookingLocale,
  productionBookingBase,
} from './env';
import type { CreateItineraryPayload, CreateItineraryResponse } from './types';

export interface CreateItineraryInput {
  productId: string | number;
  startDate: string;
  adults: number;
  rooms: number;
}

export class BookingError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'BookingError';
    this.status = status;
    this.code = code;
  }
}

export function buildItineraryPayload(input: CreateItineraryInput): CreateItineraryPayload {
  // The HofJ Booking schema requires `productId` to be an integer. The HofJ
  // Distribution API returns it as a string-encoded integer, so we coerce.
  const numericId = Number(input.productId);
  if (!Number.isFinite(numericId) || !Number.isInteger(numericId)) {
    throw new BookingError(
      `productId non valido (atteso intero, ricevuto "${input.productId}")`,
      0,
      'INVALID_PRODUCT_ID',
    );
  }

  return {
    productId: numericId,
    startDate: input.startDate,
    adults: Math.trunc(input.adults),
    rooms: Math.trunc(input.rooms),
    affiliateId,
    currency: 'EUR',
    withoutNavigations: true,
  };
}

export function itineraryEndpoint(): string {
  return `${bookingBase}/${bookingLocale}${bookingItineraryPath}`;
}

export function checkoutUrl(itineraryId: string): string {
  // Always redirect to the real booking host (the dev proxy is only used for
  // server-to-server calls during local development).
  const u = new URL(
    `${productionBookingBase}/${bookingLocale}${bookingCheckoutPath}/${encodeURIComponent(itineraryId)}`,
  );
  u.searchParams.set('withoutNavigations', 'true');
  return u.toString();
}

interface ItineraryEnvelope {
  data?: { itineraryId?: string };
  error?: { message?: string; code?: string; statusCode?: number };
}

/**
 * POST the cart payload to booking.hofj.com and return the itineraryId so the
 * caller can open it inside our in-domain checkout iframe.
 * Throws a BookingError on validation failures so the caller can surface the
 * message to the user.
 */
export async function createItinerary(
  input: CreateItineraryInput,
): Promise<{ itineraryId: string }> {
  const payload = buildItineraryPayload(input);
  const endpoint = itineraryEndpoint();

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw new BookingError(
      err instanceof Error ? err.message : 'Errore di rete',
      0,
    );
  }

  let body: ItineraryEnvelope | CreateItineraryResponse | undefined;
  try {
    body = (await response.json()) as ItineraryEnvelope;
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const err = (body as ItineraryEnvelope | undefined)?.error;
    throw new BookingError(
      err?.message ?? `HTTP ${response.status}`,
      response.status,
      err?.code,
    );
  }

  const itineraryId =
    (body as ItineraryEnvelope | undefined)?.data?.itineraryId ??
    (body as CreateItineraryResponse | undefined)?.itineraryId ??
    (body as CreateItineraryResponse | undefined)?.id;

  if (!itineraryId) {
    throw new BookingError('Risposta itinerary senza id.', response.status);
  }

  return { itineraryId: String(itineraryId) };
}

/**
 * Native form submission as a last-resort fallback. Form POSTs aren't subject
 * to CORS preflight; the booking host will respond with the HTML page directly.
 */
export function submitItineraryForm(
  payload: CreateItineraryPayload,
  endpoint: string = itineraryEndpoint(),
): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = endpoint;
  form.style.display = 'none';
  form.acceptCharset = 'UTF-8';

  for (const [key, value] of Object.entries(payload)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}
